import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, InputGroup, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUpload, FaPlus, FaTrash, FaSave, FaTimes, FaBox, FaTag, FaDollarSign, FaImage, FaInfoCircle, FaUser, FaEdit, FaCheckCircle } from 'react-icons/fa';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './EditProduct.css';

// Categories
const CATEGORIES = [
  'Textbooks',
  'Electronics',
  'Stationery',
  'Clothing',
  'Furniture',
  'Kitchen Appliances',
  'Sports Equipment',
  'Musical Instruments',
  'Lab Equipment',
  'Other'
];

// Conditions
const CONDITIONS = [
  'New',
  'Like New',
  'Very Good',
  'Good',
  'Acceptable'
];

// Initial form state

const EditProduct = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    condition: '',
    stock: '1',
    features: [''],
    images: []
  });
  
  // Preview images
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  
  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setFetchError('');
      
      try {
        // Fetch product data from API
        const response = await api.get(`/products/${id}/`);
        const product = response.data;
        
        // Verify the current user is the seller
        if (product.seller?.id !== currentUser?.id) {
          setFetchError('You do not have permission to edit this product');
          setLoading(false);
          return;
        }
        
        setFormData({
          name: product.name,
          description: product.description || '',
          price: product.price.toString(),
          category: product.category,
          condition: product.condition,
          stock: product.stockCount?.toString() || '1',
          features: product.features?.length > 0 ? product.features : [''],
          images: [] // Existing images will be handled separately
        });
        
        // Set image previews from existing images
        if (product.images?.length > 0) {
          setImagePreviewUrls(product.images);
        }
        
      } catch (err) {
        console.error('Error fetching product:', err);
        setFetchError('Failed to load product data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [id, currentUser]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = value;
    
    setFormData(prev => ({
      ...prev,
      features: updatedFeatures
    }));
  };
  
  const addFeatureField = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };
  
  const removeFeatureField = (index) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures.splice(index, 1);
    
    setFormData(prev => ({
      ...prev,
      features: updatedFeatures
    }));
  };
  
  const handleImageChange = (e) => {
    e.preventDefault();
    
    const files = Array.from(e.target.files);
    
    if (files.length + imagePreviewUrls.length > 5) {
      setError('You can upload a maximum of 5 images');
      return;
    }
    
    // Create image previews
    const newImagePreviews = [];
    
    files.forEach(file => {
      const reader = new FileReader();
      
      reader.onloadend = () => {
        newImagePreviews.push(reader.result);
        
        if (newImagePreviews.length === files.length) {
          setImagePreviewUrls(prev => [...prev, ...newImagePreviews]);
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, ...files]
          }));
        }
      };
      
      reader.readAsDataURL(file);
    });
  };
  
  const removeImage = (index) => {
    const updatedPreviews = [...imagePreviewUrls];
    updatedPreviews.splice(index, 1);
    
    const updatedImages = [...formData.images];
    updatedImages.splice(index, 1);
    
    setImagePreviewUrls(updatedPreviews);
    setFormData(prev => ({
      ...prev,
      images: updatedImages
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Form validation
    if (!formData.name.trim()) {
      setError('Product name is required');
      return;
    }
    
    if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
      setError('Please enter a valid price');
      return;
    }
    
    if (!formData.category) {
      setError('Please select a category');
      return;
    }
    
    if (!formData.condition) {
      setError('Please select a condition');
      return;
    }
    
    if (imagePreviewUrls.length === 0) {
      setError('Please upload at least one image');
      return;
    }
    
    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      
      // Append all form data
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('price', formData.price);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('condition', formData.condition);
      formDataToSend.append('stock', formData.stock || '1');
      
      // Append features as JSON string, filtering out empty ones
      const filteredFeatures = formData.features.filter(feature => feature.trim() !== '');
      formDataToSend.append('features', JSON.stringify(filteredFeatures));
      
      // Append new images
      formData.images.forEach((image) => {
        formDataToSend.append('images', image);
      });
      
      // Make PATCH request to update product
      await api.patch(`/products/${id}/`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setSuccess('Product updated successfully!');
      
      // Redirect to products list after a short delay
      setTimeout(() => {
        navigate('/seller/products');
      }, 1500);
      
    } catch (err) {
      console.error('Error updating product:', err);
      const errorMsg = err.response?.data?.message || 'Failed to update product. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && fetchError === '') {
    return (
      <Layout>
        <Container className="py-5 text-center">
          <h3>Loading product data...</h3>
        </Container>
      </Layout>
    );
  }
  
  if (fetchError) {
    return (
      <Layout>
        <Container className="py-5">
          <Alert variant="danger">
            {fetchError}
            <div className="mt-3">
              <Button variant="primary" onClick={() => navigate('/seller/products')}>
                Back to Products
              </Button>
            </div>
          </Alert>
        </Container>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <Container className="py-4 edit-product-container">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="page-header"
        >
          <h2 className="edit-product-title">
            <FaEdit className="me-3" />
            Edit Product
          </h2>
          <p className="edit-product-subtitle">Update your product details and images</p>
        </motion.div>
        
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <Alert variant="danger" className="modern-alert">
                <FaInfoCircle className="me-2" />
                {error}
              </Alert>
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <Alert variant="success" className="modern-alert">
                <FaCheckCircle className="me-2" />
                {success}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>
        
        <Form onSubmit={handleSubmit}>
          <Row>
            {/* Product Details */}
            <Col lg={8}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card className="modern-card mb-4">
                  <Card.Header className="modern-card-header">
                    <FaBox className="me-2 header-icon" />
                    <h4 className="mb-0">Product Details</h4>
                  </Card.Header>
                  <Card.Body className="modern-card-body">
                  <Form.Group className="mb-4 form-group-modern">
                    <Form.Label className="form-label-modern">
                      <FaTag className="me-2 label-icon" />
                      Product Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Textbook - Introduction to Computer Science"
                      className="form-input-modern"
                      required
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-4 form-group-modern">
                    <Form.Label className="form-label-modern">
                      <FaInfoCircle className="me-2 label-icon" />
                      Description
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Provide a detailed description of your product..."
                      className="form-input-modern"
                      required
                    />
                    <Form.Text className="form-help-text">
                      <FaInfoCircle className="me-1" />
                      Include details about the product's condition, usage history, and any defects or issues.
                    </Form.Text>
                  </Form.Group>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4 form-group-modern">
                        <Form.Label className="form-label-modern">
                          <FaDollarSign className="me-2 label-icon" />
                          Price (GH₵)
                        </Form.Label>
                        <InputGroup className="input-group-modern">
                          <InputGroup.Text className="input-group-text-modern">GH₵</InputGroup.Text>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            name="price"
                            value={formData.price}
                            onChange={handleInputChange}
                            placeholder="0.00"
                            className="form-input-modern"
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-4 form-group-modern">
                        <Form.Label className="form-label-modern">
                          <FaBox className="me-2 label-icon" />
                          Quantity Available
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          name="stock"
                          value={formData.stock}
                          onChange={handleInputChange}
                          className="form-input-modern"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Category</Form.Label>
                        <Form.Select
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Category</option>
                          {CATEGORIES.map((category, index) => (
                            <option key={index} value={category}>{category}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Condition</Form.Label>
                        <Form.Select
                          name="condition"
                          value={formData.condition}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Condition</option>
                          {CONDITIONS.map((condition, index) => (
                            <option key={index} value={condition}>{condition}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Product Features</Form.Label>
                    {formData.features.map((feature, index) => (
                      <InputGroup className="mb-2" key={index}>
                        <Form.Control
                          type="text"
                          value={feature}
                          onChange={(e) => handleFeatureChange(index, e.target.value)}
                          placeholder="e.g., Latest edition (2024)"
                        />
                        <Button 
                          variant="outline-danger"
                          onClick={() => removeFeatureField(index)}
                          disabled={formData.features.length === 1}
                        >
                          <FaTrash />
                        </Button>
                      </InputGroup>
                    ))}
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={addFeatureField}
                      className="mt-2"
                    >
                      <FaPlus className="me-1" /> Add Feature
                    </Button>
                  </Form.Group>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
            
            {/* Product Images */}
            <Col lg={4}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Card className="modern-card mb-4">
                  <Card.Header className="modern-card-header">
                    <FaImage className="me-2 header-icon" />
                    <h4 className="mb-0">Product Images</h4>
                  </Card.Header>
                  <Card.Body className="modern-card-body">
                  <Form.Group className="mb-3">
                    <Form.Label>Upload Images (Max 5)</Form.Label>
                    <div className="d-grid">
                      <Button 
                        variant="outline-primary" 
                        className="position-relative"
                        disabled={imagePreviewUrls.length >= 5}
                      >
                        <FaUpload className="me-2" />
                        {imagePreviewUrls.length === 0 ? 'Upload Images' : 'Add More Images'}
                        <Form.Control
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageChange}
                          className="position-absolute top-0 start-0 opacity-0 w-100 h-100"
                          disabled={imagePreviewUrls.length >= 5}
                        />
                      </Button>
                    </div>
                    <Form.Text className="text-muted">
                      Upload clear images of your product from different angles. First image will be the main display image.
                    </Form.Text>
                  </Form.Group>
                  
                  {imagePreviewUrls.length > 0 && (
                    <div className="image-preview-container">
                      <p className="mb-2">
                        {imagePreviewUrls.length} of 5 images uploaded
                      </p>
                      <Row className="g-2">
                        {imagePreviewUrls.map((url, index) => (
                          <Col xs={6} key={index}>
                            <div className="image-preview-item position-relative">
                              <img 
                                src={url} 
                                alt={`Preview ${index + 1}`} 
                                className="img-thumbnail"
                              />
                              <Button 
                                variant="danger" 
                                size="sm" 
                                className="position-absolute top-0 end-0 m-1 p-1"
                                onClick={() => removeImage(index)}
                              >
                                <FaTimes />
                              </Button>
                              {index === 0 && (
                                <Badge 
                                  bg="primary" 
                                  className="position-absolute bottom-0 start-0 m-1"
                                >
                                  Main
                                </Badge>
                              )}
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </Card.Body>
                </Card>
              </motion.div>
              
              {/* Seller Information */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Card className="modern-card mb-4">
                  <Card.Header className="modern-card-header">
                    <FaUser className="me-2 header-icon" />
                    <h4 className="mb-0">Seller Information</h4>
                  </Card.Header>
                  <Card.Body className="modern-card-body seller-info-body">
                  <p className="mb-1">
                    <strong>Name:</strong> {currentUser?.name || 'Not available'}
                  </p>
                  <p className="mb-1">
                    <strong>Department:</strong> {currentUser?.department || 'Not available'}
                  </p>
                  <p className="mb-1">
                    <strong>Contact:</strong> {currentUser?.phoneNumber || 'Not available'}
                  </p>
                  <p className="mb-0">
                    <strong>Location:</strong> {currentUser?.location || 'Not available'}
                  </p>
                  
                  <Alert variant="info" className="mt-3 mb-0">
                    <small className="mb-0">
                      This information will be visible to buyers. Make sure your contact details are up to date in your profile.
                    </small>
                  </Alert>
                </Card.Body>
                </Card>
              </motion.div>
              
              {/* Submit Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="d-grid gap-2"
              >
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg"
                    disabled={loading}
                    className="submit-btn-modern"
                  >
                    <FaSave className="me-2" />
                    {loading ? 'Saving...' : 'Update Product'}
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    variant="outline-secondary"
                    onClick={() => navigate('/seller/products')}
                    className="cancel-btn-modern"
                  >
                    <FaTimes className="me-2" />
                    Cancel
                  </Button>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Form>
      </Container>
    </Layout>
  );
};

export default EditProduct;
